/**
 * Obligation Editor Components
 * Type-specific editors for XACML policy obligations
 */

import { useState } from 'react'
import { Trash2, Plus, MessageSquare, Filter, EyeOff, Settings } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CatalogBrowserPopover } from './catalog-browser-popover'
import type {
  ParsedXacmlObligation,
  ParsedXacmlAttributeAssignment,
  RuleEffect,
} from '@/types/policy'
import type {
  CatalogTableSelection,
  CatalogColumnSelection,
} from '@/types/data-table'

/**
 * Obligation type identifiers
 */
export type ObligationType = 'message' | 'row-filter' | 'mask' | 'generic'

/**
 * Obligation ID URNs for each type
 */
const OBLIGATION_URNS: Record<Exclude<ObligationType, 'generic'>, string> = {
  message: 'urn:polix:obligation:message',
  'row-filter': 'urn:polix:obligation:row-filter',
  mask: 'urn:polix:obligation:column-mask',
}

/**
 * Mask pattern presets available in the mask editor
 */
export const MASK_PATTERN_PRESETS = [
  { value: 'REDACT', label: 'Full redaction' },
  { value: 'PARTIAL_RIGHT(4)', label: 'Show last 4 characters' },
  { value: 'PARTIAL_LEFT(4)', label: 'Show first 4 characters' },
  { value: 'HASH', label: 'SHA256 hash' },
  { value: 'NULL', label: 'Return NULL' },
] as const

/**
 * Detect obligation type from obligation ID string
 */
export function detectObligationType(obligationId: string): ObligationType {
  if (obligationId === OBLIGATION_URNS.message) return 'message'
  if (obligationId === OBLIGATION_URNS['row-filter']) return 'row-filter'
  if (obligationId === OBLIGATION_URNS.mask) return 'mask'
  if (obligationId === 'urn:polix:obligation:mask-column') return 'mask'
  return 'generic'
}

/**
 * Immutable helper to update an attribute assignment by attributeId
 */
function updateAssignment(
  assignments: ParsedXacmlAttributeAssignment[],
  attributeId: string,
  value: string
): ParsedXacmlAttributeAssignment[] {
  const exists = assignments.some((a) => a.attributeId === attributeId)
  if (exists) {
    return assignments.map((a) =>
      a.attributeId === attributeId
        ? { ...a, value }
        : a
    )
  }
  return [
    ...assignments,
    { attributeId, value, dataType: 'http://www.w3.org/2001/XMLSchema#string' },
  ]
}

/**
 * Immutable helper to update multiple attribute assignments at once
 */
function updateMultipleAssignments(
  assignments: ParsedXacmlAttributeAssignment[],
  updates: Record<string, string>
): ParsedXacmlAttributeAssignment[] {
  const updateEntries = Object.entries(updates)
  let result = assignments
  for (const [attributeId, value] of updateEntries) {
    result = updateAssignment(result, attributeId, value)
  }
  return result
}

/**
 * Get assignment value by attributeId
 */
function getAssignmentValue(
  assignments: ParsedXacmlAttributeAssignment[],
  attributeId: string
): string {
  const found = assignments.find((a) => a.attributeId === attributeId)
  return found ? String(found.value) : ''
}

/**
 * Catalog info badges (schema + database) shown below inputs
 */
function CatalogInfoBadges({
  schemaName,
  databaseName,
}: {
  schemaName: string
  databaseName: string
}) {
  if (!schemaName && !databaseName) return null

  return (
    <div className="flex gap-2 flex-wrap">
      {schemaName && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
          Schema: {schemaName}
        </Badge>
      )}
      {databaseName && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
          DB: {databaseName}
        </Badge>
      )}
    </div>
  )
}

/**
 * Create a prefilled obligation for a given type
 */
export function createPrefilledObligation(
  type: ObligationType,
  fulfillOn: RuleEffect
): ParsedXacmlObligation {
  switch (type) {
    case 'message':
      return {
        obligationId: OBLIGATION_URNS.message,
        fulfillOn,
        attributeAssignments: [
          {
            attributeId: 'urn:polix:obligation:message:text',
            value: '',
            dataType: 'http://www.w3.org/2001/XMLSchema#string',
          },
        ],
      }
    case 'row-filter':
      return {
        obligationId: OBLIGATION_URNS['row-filter'],
        fulfillOn,
        attributeAssignments: [
          {
            attributeId: 'table',
            value: '',
            dataType: 'http://www.w3.org/2001/XMLSchema#string',
          },
          {
            attributeId: 'predicate',
            value: '',
            dataType: 'http://www.w3.org/2001/XMLSchema#string',
          },
        ],
      }
    case 'mask':
      return {
        obligationId: OBLIGATION_URNS.mask,
        fulfillOn,
        attributeAssignments: [
          {
            attributeId: 'column',
            value: '',
            dataType: 'http://www.w3.org/2001/XMLSchema#string',
          },
          {
            attributeId: 'maskType',
            value: 'REDACT',
            dataType: 'http://www.w3.org/2001/XMLSchema#string',
          },
        ],
      }
    case 'generic':
    default:
      return {
        obligationId: '',
        fulfillOn,
        attributeAssignments: [],
      }
  }
}

/**
 * Available data types for generic editor
 */
const DATA_TYPES = [
  { value: 'http://www.w3.org/2001/XMLSchema#string', label: 'String' },
  { value: 'http://www.w3.org/2001/XMLSchema#boolean', label: 'Boolean' },
  { value: 'http://www.w3.org/2001/XMLSchema#integer', label: 'Integer' },
  { value: 'http://www.w3.org/2001/XMLSchema#double', label: 'Double' },
  { value: 'http://www.w3.org/2001/XMLSchema#date', label: 'Date' },
  { value: 'http://www.w3.org/2001/XMLSchema#time', label: 'Time' },
]

/**
 * Common props for all obligation editors
 */
interface ObligationEditorProps {
  obligation: ParsedXacmlObligation
  onChange: (obligation: ParsedXacmlObligation) => void
  onRemove: () => void
}

/**
 * Message obligation editor
 * Single textarea for the message text
 */
function MessageObligationEditor({ obligation, onChange, onRemove }: ObligationEditorProps) {
  const messageText = getAssignmentValue(
    obligation.attributeAssignments,
    'urn:polix:obligation:message:text'
  )

  const handleMessageChange = (text: string) => {
    onChange({
      ...obligation,
      attributeAssignments: updateAssignment(
        obligation.attributeAssignments,
        'urn:polix:obligation:message:text',
        text
      ),
    })
  }

  return (
    <div className="p-3 rounded-md border bg-muted/30 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-blue-500" />
          <Badge variant="secondary" className="text-xs">Message</Badge>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Message Text</Label>
        <Textarea
          value={messageText}
          onChange={(e) => handleMessageChange(e.target.value)}
          placeholder="Enter the message to return with the decision..."
          rows={2}
          className="text-sm"
        />
      </div>
    </div>
  )
}

/**
 * Row filter obligation editor
 * Inputs for table name and SQL WHERE condition with optional catalog browser
 */
function RowFilterObligationEditor({ obligation, onChange, onRemove }: ObligationEditorProps) {
  const tableName = getAssignmentValue(obligation.attributeAssignments, 'table')
  const condition = getAssignmentValue(obligation.attributeAssignments, 'predicate')
  const schemaName = getAssignmentValue(obligation.attributeAssignments, 'schema')
  const databaseName = getAssignmentValue(obligation.attributeAssignments, 'database')
  const catalogTableId = getAssignmentValue(obligation.attributeAssignments, 'catalogTableId')

  const handleFieldChange = (attributeId: string, value: string) => {
    onChange({
      ...obligation,
      attributeAssignments: updateAssignment(
        obligation.attributeAssignments,
        attributeId,
        value
      ),
    })
  }

  const handleCatalogSelect = (selection: CatalogTableSelection) => {
    onChange({
      ...obligation,
      attributeAssignments: updateMultipleAssignments(
        obligation.attributeAssignments,
        {
          table: selection.tableName,
          schema: selection.schemaName,
          database: selection.applicationName,
          catalogTableId: selection.tableId,
        }
      ),
    })
  }

  return (
    <div className="p-3 rounded-md border bg-muted/30 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-amber-500" />
          <Badge variant="secondary" className="text-xs">Row Filter</Badge>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Table Name</Label>
          <div className="flex gap-2">
            <Input
              value={tableName}
              onChange={(e) => handleFieldChange('table', e.target.value)}
              placeholder="e.g., dbo.Players"
              className="h-8 font-mono text-sm flex-1"
            />
            <CatalogBrowserPopover
              mode="table"
              onSelectTable={handleCatalogSelect}
              selectedTableId={catalogTableId || undefined}
            />
          </div>
          <CatalogInfoBadges schemaName={schemaName} databaseName={databaseName} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Filter Condition (SQL WHERE)</Label>
          <Input
            value={condition}
            onChange={(e) => handleFieldChange('predicate', e.target.value)}
            placeholder="e.g., property_id = @user_property"
            className="h-8 font-mono text-sm"
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Mask obligation editor
 * Column names input and mask pattern selector with presets, plus catalog browser
 */
function MaskObligationEditor({ obligation, onChange, onRemove }: ObligationEditorProps) {
  const columns = getAssignmentValue(obligation.attributeAssignments, 'column')
  const maskPattern = getAssignmentValue(obligation.attributeAssignments, 'maskType')
  const schemaName = getAssignmentValue(obligation.attributeAssignments, 'schema')
  const databaseName = getAssignmentValue(obligation.attributeAssignments, 'database')
  const catalogTableId = getAssignmentValue(obligation.attributeAssignments, 'catalogTableId')

  const isPreset = MASK_PATTERN_PRESETS.some((p) => p.value === maskPattern)
  const [customMode, setCustomMode] = useState(!isPreset && maskPattern !== '')

  const handleFieldChange = (attributeId: string, value: string) => {
    onChange({
      ...obligation,
      attributeAssignments: updateAssignment(
        obligation.attributeAssignments,
        attributeId,
        value
      ),
    })
  }

  const handlePatternSelect = (value: string) => {
    if (value === '__custom__') {
      setCustomMode(true)
      return
    }
    setCustomMode(false)
    handleFieldChange('maskType', value)
  }

  const handleCatalogSelect = (selection: CatalogColumnSelection) => {
    onChange({
      ...obligation,
      attributeAssignments: updateMultipleAssignments(
        obligation.attributeAssignments,
        {
          column: selection.columns.join(', '),
          schema: selection.schemaName,
          database: selection.applicationName,
          catalogTableId: selection.tableId,
        }
      ),
    })
  }

  return (
    <div className="p-3 rounded-md border bg-muted/30 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <EyeOff className="h-4 w-4 text-purple-500" />
          <Badge variant="secondary" className="text-xs">Mask Column</Badge>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Columns (comma-separated)</Label>
          <div className="flex gap-2">
            <Input
              value={columns}
              onChange={(e) => handleFieldChange('column', e.target.value)}
              placeholder="e.g., ssn, credit_card"
              className="h-8 font-mono text-sm flex-1"
            />
            <CatalogBrowserPopover
              mode="column"
              onSelectColumns={handleCatalogSelect}
              selectedTableId={catalogTableId || undefined}
            />
          </div>
          <CatalogInfoBadges schemaName={schemaName} databaseName={databaseName} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Mask Pattern</Label>
          {customMode ? (
            <div className="flex gap-2">
              <Input
                value={maskPattern}
                onChange={(e) => handleFieldChange('maskType', e.target.value)}
                placeholder="Custom pattern..."
                className="h-8 font-mono text-sm flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setCustomMode(false)}
              >
                Presets
              </Button>
            </div>
          ) : (
            <Select
              value={maskPattern}
              onValueChange={handlePatternSelect}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select pattern" />
              </SelectTrigger>
              <SelectContent>
                {MASK_PATTERN_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    <span className="font-mono">{preset.value}</span>
                    <span className="text-muted-foreground ml-2">({preset.label})</span>
                  </SelectItem>
                ))}
                <SelectItem value="__custom__">
                  Custom...
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Generic obligation editor (backward compat for unknown obligation IDs)
 * Free-text obligation ID with manual attribute assignments
 */
function GenericObligationEditor({ obligation, onChange, onRemove }: ObligationEditorProps) {
  const handleAddAssignment = () => {
    const newAssignment: ParsedXacmlAttributeAssignment = {
      attributeId: '',
      value: '',
      dataType: 'http://www.w3.org/2001/XMLSchema#string',
    }
    onChange({
      ...obligation,
      attributeAssignments: [...obligation.attributeAssignments, newAssignment],
    })
  }

  const handleAssignmentChange = (index: number, assignment: ParsedXacmlAttributeAssignment) => {
    const updated = obligation.attributeAssignments.map((a, i) =>
      i === index ? assignment : a
    )
    onChange({ ...obligation, attributeAssignments: updated })
  }

  const handleRemoveAssignment = (index: number) => {
    onChange({
      ...obligation,
      attributeAssignments: obligation.attributeAssignments.filter((_, i) => i !== index),
    })
  }

  return (
    <div className="p-3 rounded-md border bg-muted/30 space-y-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2 mb-1">
            <Settings className="h-4 w-4 text-muted-foreground" />
            <Badge variant="outline" className="text-xs">Generic</Badge>
          </div>
          <Label className="text-xs text-muted-foreground">Obligation ID</Label>
          <Input
            value={obligation.obligationId}
            onChange={(e) =>
              onChange({ ...obligation, obligationId: e.target.value })
            }
            placeholder="e.g., urn:polix:obligation:custom"
            className="h-8 font-mono text-sm"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0 mt-7"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Attribute Assignments */}
      <div className="space-y-2 pl-3 border-l-2 border-muted">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Attribute Assignments</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={handleAddAssignment}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add
          </Button>
        </div>
        {obligation.attributeAssignments.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No attribute assignments.</p>
        ) : (
          obligation.attributeAssignments.map((aa, aaIndex) => (
            <div key={aaIndex} className="flex items-center gap-2">
              <Input
                value={aa.attributeId}
                onChange={(e) =>
                  handleAssignmentChange(aaIndex, { ...aa, attributeId: e.target.value })
                }
                placeholder="Attribute ID"
                className="h-7 font-mono text-xs flex-1"
              />
              <Input
                value={String(aa.value)}
                onChange={(e) =>
                  handleAssignmentChange(aaIndex, { ...aa, value: e.target.value })
                }
                placeholder="Value"
                className="h-7 font-mono text-xs flex-1"
              />
              <Select
                value={aa.dataType ?? 'http://www.w3.org/2001/XMLSchema#string'}
                onValueChange={(value) =>
                  handleAssignmentChange(aaIndex, { ...aa, dataType: value })
                }
              >
                <SelectTrigger className="h-7 text-xs w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATA_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                onClick={() => handleRemoveAssignment(aaIndex)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

/**
 * Routing obligation editor
 * Detects obligation type and renders the appropriate sub-editor
 */
export function ObligationEditor(props: ObligationEditorProps) {
  const type = detectObligationType(props.obligation.obligationId)

  switch (type) {
    case 'message':
      return <MessageObligationEditor {...props} />
    case 'row-filter':
      return <RowFilterObligationEditor {...props} />
    case 'mask':
      return <MaskObligationEditor {...props} />
    case 'generic':
    default:
      return <GenericObligationEditor {...props} />
  }
}
